import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, CircularProgress } from "@mui/material";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getApiDocsHtml } from "app/services/api/apiDocs";

const USER_TYP_ADMIN = 1;
const AUDIENCES = ["partner", "internal"];

export default function ApiDocsViewerPage() {
  const { t } = useTranslation();
  const { audience: audienceParam } = useParams();
  const audience = AUDIENCES.includes(audienceParam) ? audienceParam : "partner";

  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const isAdmin = Number(authUser?.type ?? authUser?.typ) === USER_TYP_ADMIN;
  // Partner customers (Business Partner = 2, Service Partner = 7) may view the PARTNER docs;
  // the internal docs stay admin-only.
  const customerType = Number(
    authUser?.customer?.customerType ?? authUser?.customer?.customer_type
  );
  const isPartner = customerType === 2 || customerType === 7;
  const canView = audience === "partner" ? isAdmin || isPartner : isAdmin;

  const [html, setHtml] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getApiDocsHtml(audience, "sk")
      .then((res) => {
        if (!cancelled) setHtml(res);
      })
      .catch(() => {
        if (!cancelled)
          setError(
            t("pages.apiDocs.loadError", {
              defaultValue: "Dokumentáciu sa nepodarilo načítať.",
            })
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [audience, t]);

  if (!canView) {
    return (
      <Box sx={{ px: 0 }}>
        <Alert severity="error">
          {t("pages.apiDocs.noAccess", {
            defaultValue: "Táto sekcia je dostupná len pre administrátorov.",
          })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", height: "100%", minHeight: 600, bgcolor: "#fff" }}>
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.7)",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {!error && (
        <iframe
          title={audience}
          srcDoc={html}
          style={{ width: "100%", height: "100%", minHeight: 600, border: "none" }}
        />
      )}
    </Box>
  );
}
